<template>
  <div id="design-comment">
    <div class="title-area clearfix">
      <div class="title">
        {{ $t('tenantProjects.submission.comment.cpReview') }}
      </div>
      <div class="records">
        <div
          v-show="displayUnderReviewText"
          class="update-history__text"
        >
          {{ underReviewText }}
        </div>
        <div>
          <i
            v-if="isStarted"
            class="font-gray"
          >
            {{ reviewStartTime }}
          </i>
        </div>
      </div>
    </div>
    <div v-if="!isStarted">
      <div
        v-if="canComment && startReview"
        class="start-btn-wrapper"
      >
        <button
          class="start-btn btn--darkblue"
          @click="startDesignReview"
        >
          {{ deep(activeSubmission, 'techComment') ? $t('action.comment') : $t('action.startReview') }}
        </button>
      </div>
      <div v-else>
        <p class="design-comment-not-started">
          {{ $t('tenantProjects.submission.comment.designNotStarted') }}.
        </p>
      </div>
    </div>

    <div v-else>
      <div
        v-if="!isPic && !isSubmitted && !designComment.description"
        class="under-review-hint__text"
      >
        {{ underReviewText }}
      </div>

      <div
        v-else
        class="design-comment-action-container"
      >
        <div
          v-if="isSubmitted"
          class="design-comment-action"
        >
          <span>{{ $t('status.name') }}:</span>
          <div
            v-for="rec in actionHistory"
            :key="rec.id"
            class="design-comment-history clearfix"
          >
            <span>{{ actionTexts[rec.comment] }}</span>
            <span class="update-history__text">{{ rec.user.displayName }} {{ $t('on') }} {{ dateUtil.format(rec.updatedAt, 'DD MMM YY HH:mm') }}</span>
          </div>
        </div>
        <div
          v-if="isPic && canComment"
          class="design-comment__textarea"
        >
          <textarea
            v-autosize
            v-hkldinput
            type="text"
            class="comment-text autosize"
            :value="designComment.description"
            @change="event => updateDescription(event.target.value)"
          />
        </div>
        <template v-else-if="isPic || isSubmitted || designComment.description">
          <hr v-if="isSubmitted">
          <p class="design-comment-text">
            <pre>{{ designComment.description || '--' }}</pre>
          </p>
        </template>
        <div class="design-comment-history">
          <div
            v-for="rec in commentHistory"
            :key="rec.id"
            class="clearfix"
          >
            <div class="update-history__text">
              {{ $t('workPermit.reviewedBy', [rec.user.displayName, dateUtil.format(rec.updatedAt, 'DD MMM YY HH:mm')]) }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from 'vuex'
import { actionTexts, CommentTypes, CommentObject, CommentActionTypes } from './consts'
import { DesignBatchStatus } from '@/utils/locale'
import mutationTypes from '@/store/mutation-types'
import EventBus from '@/utils/eventBus'
import dateUtil from '@/utils/dateUtil'
import {
  hasSubmissionReviewStarted,
  isPendingSubmissionUnderReview,
} from '@/modules/tenantProjects/utils'

export default {
  name: 'DesignComment',
  props: {
    canComment: Boolean,
  },
  data () {
    return {
      DesignBatchStatus,
      dateUtil,
      deep,
    }
  },
  computed: {
    ...mapGetters('tenantProject', ['currentTenantProject', 'currentTenantSubmission', 'hasPendingSubmission', 'currentDesignReviewer', 'startReview', 'activeSubmission']),
    ...mapGetters('user', ['currentUser']),
    actionTexts () {
      return actionTexts()
    },
    designComment () {
      return JSON.parse(JSON.stringify(deep(this.currentTenantSubmission, 'designComment') || {}))
    },
    submittedDesignComment () {
      return JSON.parse(JSON.stringify(deep(this.currentTenantSubmission, 'submittedDesignComment') || {}))
    },
    submittedDate () {
      return this.submittedDesignComment.submittedDate
    },
    isStarted () {
      return this.hasPendingSubmission && this.currentDesignReviewer
    },
    isPic () {
      return this.currentDesignReviewer && (this.currentDesignReviewer.id === this.currentUser.id)
    },
    isSubmitted () {
      return !!this.submittedDesignComment.submittedDate
    },

    underReviewText () {
      return this.$t('tenantProjects.submission.submissionUnderReview', [
        deep(this.currentTenantSubmission, 'designManager.displayName')])
    },
    reviewStartTime () {
      return this.$t('tenantProjects.submission.startedOn', [
        dateUtil.formatDate(deep(this.currentTenantSubmission, 'designComment.startedAt'), true)])
    },
    lastDesignCommentActionText () {
      return this.hasPendingSubmission && (deep(this.currentTenantSubmission, 'actionHistory') || [])
        .filter(rec => rec.actionType === CommentActionTypes.REVIEW && rec.commentTypes.includes(CommentTypes.DESIGN) && !rec.isNewRec)
        .map(rec => this.$t('project.submission.lastReviewedBy', [rec.user.displayName, dateUtil.format(rec.updatedAt, 'DD MMM YY HH:mm')]))[0]
    },
    actionHistory () {
      return this.hasPendingSubmission && (deep(this.currentTenantSubmission, 'actionHistory') || [])
        .filter(rec => rec.comment && rec.actionType === CommentActionTypes.REVIEW && rec.commentTypes.includes(CommentTypes.DESIGN) && !rec.isNewRec)
    },
    commentHistory () {
      return this.hasPendingSubmission && (deep(this.currentTenantSubmission, 'actionHistory') || [])
        .filter(rec => rec.actionType === CommentActionTypes.UPDATE && rec.commentObject === CommentObject.COMMENT && rec.commentTypes.includes(CommentTypes.DESIGN) && !rec.isNewRec)
    },

    currentTenantSubmissionCompleted () {
      return Boolean(this.currentTenantSubmission && this.currentTenantSubmission.completedDate)
    },
    displayUnderReviewText () {
      return !this.currentTenantSubmissionCompleted &&
        !this.isPic &&
        (
          this.isSubmitted
            ? this.designComment.comment === DesignBatchStatus.PROCEED_CP_FOLLOW_UP
            : this.designComment.description
        )
    },
  },
  methods: {
    ...mapActions('tenantProject', ['createTenantSubmission', 'loadTenantProject', 'updateCurrentStateSubmissionByPath', 'updateCurrentTenantSubmission']),
    ...mapMutations('tenantProject', {
      updateFieldByPath: mutationTypes.TENANT_PROJECT_UPDATE_FIELD_PATH,
    }),
    updateDescription (desc) {
      this.designComment.description = desc
      this.updateGlobalDesignComment(this.designComment)
    },
    updateGlobalDesignComment (designComment) {
      this.updateCurrentStateSubmissionByPath({
        path: ['designComment'],
        newValue: designComment,
      }, designComment)
    },
    async startDesignReview () {
      if (!deep(this.currentTenantProject, 'id')) { return }
      const priorSubmission = this.currentTenantSubmission
      if (!this.hasPendingSubmission) {
        await this.createTenantSubmission({
          projectId: this.currentTenantProject.id,
          submission: {
            designManagedBy: this.currentUser.id,
            designComment: {startedAt: new Date()},
          },
        })
        await this.loadTenantProject(this.$router.currentRoute.params.id)
        if (!isPendingSubmissionUnderReview(priorSubmission)) {
          EventBus.$emit('tenant_submission_created')
        }
      } else {
        this.updateCurrentStateSubmissionByPath({
          path: ['designManagedBy'],
          newValue: this.currentUser.id,
        })
        this.updateCurrentStateSubmissionByPath({
          path: ['designComment'],
          newValue: {
            startedAt: new Date(),
            ...this.designComment,
          },
        })
        await this.updateCurrentTenantSubmission({})
        await this.loadTenantProject(this.$router.currentRoute.params.id)
        if (!hasSubmissionReviewStarted(priorSubmission)) {
          EventBus.$emit('tenant_submission_assign_batch')
        }
      }
    },
  },
}
</script>

<style lang="scss" scoped>
$titleMargin: 10px;
#design-comment {
  padding: 0px 15px 0 12px;
  background-color: white;
  .font-gray {
    display: inline-block;
    font-size: 11px;
    color: #878787;
    font-weight: normal;
    padding-left: 15px;
  }
  .title-area {
    margin-top: $titleMargin;
    display: flex;
    .records {
      flex: 1;
      text-align: right;
    }
  }
  .title {
    font-weight: bold;
    float: left;
  }
  .design-comment__textarea {
    margin-top: 15px;
  }
  .radioClass {
    width: 16px;
    height: 16px;
    margin: 2px 0 0 0;
    display: inline-block;
  }
  .radio-label {
    font-family: Verdana;
    color: #0C3A58;
    display: inline-block;
  }
  .radio-item {
    margin: 10px 0;
  }
  .comment-text{
    border: solid #e0e1e4 1px;
    width: 100%;
    margin-bottom: 6px;
    word-break: break-word;
    text-align: justify;
  }
  .design-comment-action, .design-comment-text {
    margin: 6px 0;
  }
  .design-comment-history {
    margin-bottom: 6px;
  }
  .design-comment-text {
    text-align: justify;
    pre {
      word-break: normal;
      overflow-wrap: anywhere;
    }
  }
}
.start-btn {
  padding: 10px 20px;
  font-size: 14px;
  font-family: Verdana;
  border: solid #415867 1px;
  display: block;
  margin: 0 auto;
  cursor: pointer;
}
.start-btn-wrapper {
  padding: 15px 0 26px 0;
}
.design-comment-action-container .update-history__text{
  float:right;
}
.under-review-hint__text, .design-comment-not-started {
  color: #878787;
  font-size: 12px;
  text-align: center;
  margin: 20px 0;
}
.update-history__text {
    color: #878787;
    font-size: 11px;
    padding-left: 0 10px;
    font-style: italic;
    line-height: 20px;
    p, div {
      margin-bottom: 0;
      text-align: right;
    }
  }
hr {
  border: solid 1px #5d7c9d;
  opacity: 0.21;
  margin: 0;
}
</style>
